import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, FileSpreadsheet, Landmark, ShieldCheck, TableProperties, Sparkles, LineChart } from "lucide-react";

const features = [
  { icon: TableProperties, title: "Structured table reconstruction", body: "Rows, columns and headers are rebuilt — not dumped as plain OCR text." },
  { icon: LineChart, title: "Cash-flow & credit analytics", body: "Monthly aggregation, EMI detection, recurring patterns, stability factors." },
  { icon: ShieldCheck, title: "Auditable by design", body: "Confidence scores, balance validation and duplicate review before analysis." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 h-16 border-b border-border bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex h-full max-w-7xl items-center justify-between px-6">
          <div className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-md bg-primary text-primary-foreground">
              <Landmark className="h-4 w-4" />
            </div>
            <span className="font-display text-lg font-extrabold tracking-tight">GrowRupee</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" data-testid="nav-login-btn">Log in</Button></Link>
            <Link to="/signup"><Button className="rounded-full" data-testid="nav-signup-btn">Get started</Button></Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden border-b border-border">
        <img src="https://images.unsplash.com/photo-1493397212122-2b85dda8106b?w=1800&q=80"
             alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="noise-overlay absolute inset-0 opacity-[0.05]" />
        <div className="relative mx-auto grid max-w-7xl gap-16 px-6 py-24 lg:grid-cols-[1.15fr_1fr] lg:py-32">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              <Sparkles className="h-3 w-3" /> Smart Statement Analyzer
            </span>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl">
              Turn Bank Statements into Excel, Insights &amp; Actionable Financial Analysis.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground">
              Upload scanned statements, PDFs or phone photos. GrowRupee reconstructs the transaction
              table, validates every balance, categorises each entry and produces a review-ready
              financial assessment you can export to Excel or PDF.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/signup">
                <Button size="lg" className="rounded-full" data-testid="hero-start-btn">
                  Start free <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-full bg-white" data-testid="hero-login-btn">
                  I already have an account
                </Button>
              </Link>
            </div>
          </div>

          <div className="grid gap-4 self-center">
            <div className="rounded-lg border border-border bg-white p-6 transition-transform duration-200 hover:-translate-y-0.5">
              <FileSpreadsheet className="h-5 w-5 text-primary" />
              <h3 className="mt-4 font-display text-xl font-semibold">Image/PDF → Excel</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Convert tables, invoices, lists and documents into editable Excel spreadsheets.
              </p>
            </div>
            <div className="rounded-lg border border-border bg-white p-6 transition-transform duration-200 hover:-translate-y-0.5">
              <Landmark className="h-5 w-5 text-primary" />
              <h3 className="mt-4 font-display text-xl font-semibold">Bank Statement Analyzer</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Extract transactions, analyze cash flow and generate a financial assessment.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Why GrowRupee</p>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {features.map((f) => (
            <div key={f.title} className="rounded-lg border border-border bg-white p-6 hover:shadow-sm">
              <f.icon className="h-5 w-5 text-primary" />
              <h3 className="mt-4 font-display text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-border bg-white">
        <div className="mx-auto max-w-7xl px-6 py-8 text-xs text-muted-foreground">
          Analytical estimates are generated from extracted bank-statement data and may contain OCR or
          classification errors. Users should verify important information against the original statement.
        </div>
      </footer>
    </div>
  );
}
